import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

type Merchant = { id: string; username: string; company_name?: string | null };
const errorMessage = (error: any) => typeof error.response?.data?.detail === 'string'
    ? error.response.data.detail : 'Не удалось выполнить запрос. Попробуйте ещё раз.';

export const CustomerAntifraudPanel: React.FC<{ readOnly: boolean }> = ({ readOnly }) => {
    const [merchants, setMerchants] = useState<Merchant[]>([]);
    const [merchantId, setMerchantId] = useState('');
    const [optionsLoading, setOptionsLoading] = useState(true);
    const [optionsError, setOptionsError] = useState('');
    const [reload, setReload] = useState(0);

    useEffect(() => {
        let active = true;
        setOptionsLoading(true); setOptionsError('');
        adminAPI.getTransactionFilterOptions().then(result => {
            if (active) setMerchants(result.merchants);
        }).catch(error => { if (active) setOptionsError(errorMessage(error)); })
            .finally(() => { if (active) setOptionsLoading(false); });
        return () => { active = false; };
    }, [reload]);

    return <Card>
        <CardHeader>
            <CardTitle>Исключения антифрода</CardTitle>
            <CardDescription>Разрешение снимает ограничение «3 транзакции за 5 минут» для конкретного клиента выбранного мерчанта на ввод и вывод. Ограничения по суммам, реквизитам и балансам сохраняются.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
            <div className="space-y-2">
                <Label htmlFor="antifraud-merchant">Мерчант</Label>
                <select id="antifraud-merchant" value={merchantId} disabled={optionsLoading}
                    onChange={event => setMerchantId(event.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                    <option value="">{optionsLoading ? 'Загрузка мерчантов…' : 'Выберите мерчанта'}</option>
                    {merchants.map(merchant => <option key={merchant.id} value={merchant.id}>
                        {merchant.company_name ? `${merchant.company_name} · ${merchant.username}` : merchant.username}
                    </option>)}
                </select>
            </div>
            {optionsError && <div role="alert" className="space-y-2"><p className="text-red-600 dark:text-red-300">{optionsError}</p><Button variant="outline" onClick={() => setReload(value => value + 1)}>Повторить загрузку</Button></div>}
            {merchantId && <MerchantPermissions key={merchantId} merchantId={merchantId} readOnly={readOnly} />}
        </CardContent>
    </Card>;
};

const MerchantPermissions: React.FC<{ merchantId: string; readOnly: boolean }> = ({ merchantId, readOnly }) => {
    const [customerIds, setCustomerIds] = useState<string[]>([]);
    const [customerId, setCustomerId] = useState('');
    const [loading, setLoading] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [reload, setReload] = useState(0);

    useEffect(() => {
        let active = true;
        setLoading(true); setError('');
        adminAPI.getCustomerAntifraudPermissions(merchantId).then(result => {
            if (active) { setCustomerIds(result.customer_ids); setLoaded(true); }
        }).catch(error => { if (active) setError(errorMessage(error)); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [merchantId, reload]);

    const save = async (id: string, allowed: boolean) => {
        setBusy(true); setError(''); setNotice('');
        try {
            const result = await adminAPI.setCustomerAntifraudPermission(merchantId, id, allowed);
            setCustomerIds(result.customer_ids);
            if (allowed) setCustomerId('');
            setNotice(allowed ? 'Разрешение добавлено.' : 'Разрешение отозвано. Для новых запросов действуют обычные проверки.');
        } catch (error) { setError(errorMessage(error)); }
        finally { setBusy(false); }
    };

    return <div className="space-y-4">
        <p className="text-sm text-muted-foreground">Customer ID должен точно совпадать с переданным мерчантом: customer_account_id в API v2 или user_id в API v1. В API v2 без Customer ID исключение не применяется.</p>
        {readOnly && <p className="text-sm text-muted-foreground">Изменять разрешения может администратор с полным доступом.</p>}
        <form onSubmit={event => { event.preventDefault(); if (customerId.trim()) void save(customerId, true); }}>
            <fieldset disabled={readOnly || busy || loading || !loaded} className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[200px] space-y-2"><Label htmlFor="antifraud-customer">Customer ID</Label>
                    <Input id="antifraud-customer" value={customerId} onChange={event => setCustomerId(event.target.value)} required maxLength={256} placeholder="Например, customer-123" />
                </div>
                <Button type="submit" disabled={!customerId.trim() || customerIds.includes(customerId)}>{busy ? 'Сохранение…' : 'Добавить разрешение'}</Button>
            </fieldset>
        </form>
        {error && <div role="alert" className="space-y-2"><p className="text-red-600 dark:text-red-300">{error}</p>{!loaded && <Button variant="outline" disabled={loading} onClick={() => setReload(value => value + 1)}>Повторить загрузку</Button>}</div>}
        {notice && <p role="status" className="text-sm text-emerald-700 dark:text-emerald-300">{notice}</p>}
        {loading ? <p role="status">Загрузка разрешений…</p> : loaded && <div className="space-y-2">
            <h3 className="font-medium">Разрешённые клиенты · {customerIds.length}</h3>
            {!customerIds.length && <p className="text-sm text-muted-foreground">Для этого мерчанта исключений пока нет.</p>}
            {customerIds.map(id => <div key={id} className="flex items-center justify-between gap-4 rounded-md border p-3">
                <span className="font-mono text-sm break-all whitespace-pre-wrap">{id}</span>
                <Button variant="outline" disabled={readOnly || busy} onClick={() => void save(id, false)}>Отозвать разрешение</Button>
            </div>)}
        </div>}
    </div>;
};
